import React, { useEffect, useMemo, useRef, useState } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PaletteIcon from '@mui/icons-material/Palette';
import ImageIcon from '@mui/icons-material/Image';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import LanguageIcon from '@mui/icons-material/Language';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  styled,
  TextField,
  Typography,
} from '@mui/material';

/** Live preview steps: which screen is shown in the phone mockup. */
export const PREVIEW_STEPS = ['terms', 'document'] as const;
export type PreviewStepId = (typeof PREVIEW_STEPS)[number];

/** Which form sections to show for each preview step (so fields match the visible screen). */
export const SECTIONS_BY_PREVIEW_STEP: Record<PreviewStepId, string[]> = {
  terms: ['branding', 'colorsTypography'],
  document: ['progressBar', 'sdkColors'],
};

const PhonePreview = styled(Box)(({ theme }) => ({
  width: '280px',
  height: '560px',
  border: '10px solid #1a1a1a',
  borderRadius: '36px',
  background: '#fff',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: theme.shadows[8],
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100px',
    height: '24px',
    background: '#1a1a1a',
    borderRadius: '0 0 16px 16px',
    zIndex: 1,
  },
}));

const SectionTitle = ({
  icon: Icon,
  children,
}: {
  icon: React.ElementType;
  children: React.ReactNode;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    <Icon sx={{ fontSize: 20, color: 'primary.main' }} />
    <Typography variant="subtitle1" fontWeight={600} color="text.primary">
      {children}
    </Typography>
  </Box>
);

const SPINNER_TRACK_PATH =
  'M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z';
const SPINNER_ARC_PATH =
  'M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z';

function LoadingSpinnerPreview({
  spinnerColor,
  spinnerFill,
}: {
  spinnerColor: string;
  spinnerFill: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: { xs: 'center', md: 'flex-start' },
        gap: 0.5,
        flexShrink: 0,
        width: { xs: '100%', md: 88 },
        maxWidth: 88,
        minHeight: 88,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        component="svg"
        aria-hidden
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        sx={{
          width: 32,
          height: 32,
          animation: 'flowSpinnerSpin 1s linear infinite',
          '@keyframes flowSpinnerSpin': {
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' },
          },
        }}
      >
        <path d={SPINNER_TRACK_PATH} fill={spinnerFill} />
        <path d={SPINNER_ARC_PATH} fill={spinnerColor} />
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 11 }}
      >
        Preview
      </Typography>
    </Box>
  );
}

const ColorRow = ({
  label,
  value,
  onChange,
  helperText,
  onEditStart,
  onEditEnd,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  helperText?: string;
  onEditStart?: () => void;
  onEditEnd?: () => void;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
    <Box
      component="label"
      sx={{
        position: 'relative',
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: '50%',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: value,
        cursor: 'pointer',
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
      }}
    >
      <Box
        component="input"
        type="color"
        value={value}
        onClick={onEditStart}
        onFocus={onEditStart}
        onBlur={onEditEnd}
        onInput={(e: React.FormEvent<HTMLInputElement>) =>
          onChange((e.target as HTMLInputElement).value)
        }
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: 'pointer',
          opacity: 0,
          '&::-webkit-color-swatch-wrapper': { padding: 0 },
          '&::-webkit-color-swatch': { border: 'none', borderRadius: 0 },
          '&::-moz-color-swatch': { border: 'none', borderRadius: 0 },
        }}
      />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        fontWeight={600}
        color="text.primary"
        sx={{ fontSize: '0.8125rem', lineHeight: 1.3 }}
      >
        {label}
      </Typography>
      {helperText && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{
            fontSize: '0.65rem',
            lineHeight: 1.3,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            mt: 0.25,
          }}
        >
          {helperText}
        </Typography>
      )}
      <Typography
        variant="caption"
        component="span"
        sx={{
          fontFamily: 'monospace',
          color: 'text.secondary',
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

const ColorGrid = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      gap: 2,
      alignItems: 'start',
    }}
  >
    {children}
  </Box>
);

const designCardSx = {
  mb: 3,
  borderRadius: 3,
  overflow: 'hidden',
  bgcolor: 'grey.50',
  borderColor: 'divider',
};

// SVGs loaded via <img> require Content-Type: image/svg+xml from the server.
// Cloud-storage proxies often return application/octet-stream, causing the
// browser to refuse rendering. Fetching and re-wrapping as a blob fixes this
// without any backend changes. PNG/JPG are passed through untouched.
function useSafeLogoSrc(src: string | null | undefined): string | null {
  const [safeSrc, setSafeSrc] = useState<string | null>(src ?? null);

  useEffect(() => {
    setSafeSrc(src ?? null);
    if (!src || src.startsWith('data:') || !/\.svg(\?|$)/i.test(src)) return;

    let objectUrl: string | null = null;
    let cancelled = false;

    fetch(src)
      .then(r => r.blob())
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(
          new Blob([blob], { type: 'image/svg+xml' }),
        );
        setSafeSrc(objectUrl);
      })
      .catch(() => {
        /* keep original src as fallback */
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  return safeSrc;
}

const UploadZone = ({
  onUpload,
  onRemove,
  value,
  label,
  hint,
}: {
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  value: string | null;
  label: string;
  hint: string;
}) => {
  const safeValue = useSafeLogoSrc(value);
  return (
    <Box
      sx={{
        border: '2px dashed',
        borderColor: value ? 'transparent' : 'divider',
        borderRadius: 2,
        p: 2,
        bgcolor: value ? 'action.hover' : 'transparent',
        transition: 'border-color 0.2s, background-color 0.2s',
        '&:hover': {
          borderColor: 'primary.light',
          bgcolor: value ? undefined : 'action.hover',
        },
      }}
    >
      {safeValue ? (
        <Box sx={{ position: 'relative', display: 'block', width: '100%' }}>
          <img
            src={safeValue}
            alt={label}
            style={{
              display: 'block',
              width: '100%',
              height: 80,
              objectFit: 'contain',
              borderRadius: 8,
            }}
          />
          <Button
            size="small"
            onClick={onRemove}
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              minWidth: 28,
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: 'error.main',
              color: 'error.contrastText',
              '&:hover': { backgroundColor: 'error.dark' },
              boxShadow: 2,
            }}
          >
            ×
          </Button>
        </Box>
      ) : (
        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUploadIcon />}
          fullWidth
          sx={{ py: 1.5 }}
        >
          {label}
          <input
            type="file"
            hidden
            accept=".svg,.png,.jpg,.jpeg"
            onChange={onUpload}
          />
        </Button>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 1 }}
      >
        {hint}
      </Typography>
    </Box>
  );
};

/** SDK color palette keys with human-readable labels for the form. */
const COLOR_PALETTE_ENTRIES: {
  key: keyof FlowDesignColorPalette;
  label: string;
}[] = [
  { key: 'accentColor', label: 'Accent' },
  { key: 'appBarTitleColor', label: 'App bar title' },
  { key: 'buttonBgColor', label: 'Button background' },
  { key: 'buttonTextColor', label: 'Button text' },
  { key: 'cameraHeaderBgColor', label: 'Camera header background' },
  { key: 'docSelectContainerBgColor', label: 'Doc select container' },
  { key: 'incodeHighlightedTextColor', label: 'Incode highlighted text' },
  { key: 'incodeTextColor', label: 'Incode text' },
  { key: 'modalBgColor', label: 'Modal background' },
  { key: 'ocrFieldsBgColor', label: 'OCR fields background' },
  { key: 'ocrInputTextColor', label: 'OCR input text' },
  { key: 'primaryBgColor', label: 'Primary background' },
  { key: 'primaryTextColor', label: 'Primary text' },
  { key: 'reasonActiveBgColor', label: 'Reason active background' },
  { key: 'reasonHoverBgColor', label: 'Reason hover background' },
  { key: 'sdkSpecialTextColor', label: 'SDK special text' },
  { key: 'themeErrorColor', label: 'Theme error' },
  { key: 'themeRadioAccentColor', label: 'Theme radio accent' },
  { key: 'themeRadioColor', label: 'Theme radio' },
  { key: 'themeSecondaryTextColor', label: 'Theme secondary text' },
];

// const fonts = [
//   'Arial',
//   'Helvetica',
//   'Times New Roman',
//   'Georgia',
//   'Verdana',
//   'Tahoma',
//   'Trebuchet MS',
//   'Impact',
//   'Comic Sans MS',
//   'Courier New',
//   'Lucida Console',
//   'Palatino',
//   'Garamond',
//   'Bookman',
//   'Avant Garde',
//   'Didot',
//   'Futura',
//   'Bodoni',
//   'Rockwell',
//   'Century Gothic',
// ];

/** SDK/theme color palette (design.color) sent to API. */
export interface FlowDesignColorPalette {
  abortButtonColor?: string;
  accentColor?: string;
  appBarBackgroundColor?: string;
  appBarTitleColor?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  cameraHeaderBgColor?: string;
  checkboxAccentColor?: string;
  docSelectBgColor?: string;
  docSelectContainerBgColor?: string;
  incodeHighlightedTextColor?: string;
  incodeTextColor?: string;
  loadingSpinnerColor?: string;
  loadingSpinnerFill?: string;
  modalBgColor?: string;
  ocrFieldsBgColor?: string;
  ocrInputTextColor?: string;
  primaryBgColor?: string;
  primaryTextColor?: string;
  progressBarBgColor?: string;
  reasonActiveBgColor?: string;
  reasonHoverBgColor?: string;
  sdkSpecialTextColor?: string;
  themeErrorColor?: string;
  themeRadioAccentColor?: string;
  themeRadioColor?: string;
  themeSecondaryTextColor?: string;
  tutorialAccentColor?: string;
}

/** Nested translations on flow design (`design.translations` → runtime locale JSON). */
export type FlowDesignTranslations = {
  de?: null | Record<string, unknown>;
  en?: {
    abortpage?: Record<string, string>;
    userConsent?: Record<string, string>;
    client?: {
      userConsent?: Record<string, string>;
    };
    [key: string]: unknown;
  };
  es?: null | Record<string, unknown>;
  fr?: null | Record<string, unknown>;
  nl?: null | Record<string, unknown>;
  tr?: null | Record<string, unknown>;
  'zh-cn'?: null | Record<string, unknown>;
  [locale: string]: unknown;
};

/** Subset of flow design sent to API – syncs with parent when used in Configuration. */
export interface FlowDesignFormPartial {
  appBarLogo?: string;
  appBarLogoFile?: File | null;
  /** Logo height in px (controls app bar logo size). */
  appBarLogoHeight?: number;
  /** Optional base64/data-url banner shown above main content. */
  bannerImg?: string | null;
  /** Optional banner image file uploaded to API (multipart). */
  bannerImageFile?: File | null;
  appBarClass?: string;
  appBarButtonColor?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  secondaryButtonColor?: string;
  documentLinkBgColor?: string;
  allowedIdsColor?: string;
  allowedIdsTextColor?: string;
  textColor?: string;
  backgroundColor?: string;
  progressBarTrackColor?: string;
  progressBarFillColor?: string;
  color?: Partial<FlowDesignColorPalette>;
  translations?: FlowDesignTranslations;
}

const DEFAULT_CANCEL_VERIFICATION_TEXT = 'Cancel verification';

function readCancelVerificationText(
  design: FlowDesignFormPartial | undefined,
): string {
  const userConsent =
    design?.translations?.en?.userConsent?.cancelVerification ??
    design?.translations?.en?.client?.userConsent?.cancelVerification;
  if (typeof userConsent === 'string' && userConsent.trim()) {
    return userConsent.trim();
  }

  /** Abort page button label (separate from terms step). */
  const abortBtn = design?.translations?.en?.abortpage?.cancelVerificationBtn;
  if (typeof abortBtn === 'string' && abortBtn.trim()) return abortBtn.trim();
  /** Older saves stored an i18n path in `cancelVerificationTranslationKey`. */
  const legacy = (design as any)?.cancelVerificationTranslationKey;
  if (typeof legacy === 'string' && legacy.trim()) {
    const s = legacy.trim();
    if (s.includes('.') && !/\s/.test(s))
      return DEFAULT_CANCEL_VERIFICATION_TEXT;
    return s;
  }
  return DEFAULT_CANCEL_VERIFICATION_TEXT;
}

/** Sample `en.client.userConsent` copy for CMS preview (aligned with runtime JSON). */
const PREVIEW_CLIENT_USER_CONSENT: Record<string, string> = {
  acceptToContinue: 'Please accept to continue',
  and: 'and',
  cancelVerification: 'Cancel verification',
  consentMessage:
    'I hereby consent to Betsson processing my biometric data for the purpose of verifying my identity.',
  moreDetails: 'Find more details in the ',
  privacyPolicy: 'privacy policy',
  privacyPolicyLink: '#',
  startVerification: 'Start verification',
  termsAndConditionsLink: '#',
  termsOfService: 'general terms and conditions.',
  title: 'Terms & Conditions',
};

const PREVIEW_EN_CLIENT = {
  userConsent: PREVIEW_CLIENT_USER_CONSENT,
};

/** Resolve `client.userConsent.*` keys to English preview strings; unknown keys return null. */
function resolveClientTranslationKey(key: string): string | null {
  const parts = key.trim().split('.').filter(Boolean);
  if (parts.length === 0) return null;
  if (parts[0] === 'client') parts.shift();
  let cur: unknown = PREVIEW_EN_CLIENT;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return null;
    cur = (cur as Record<string, unknown>)[p];
  }
  return typeof cur === 'string' ? cur : null;
}

/** Default SDK/theme color palette (design.color). */
const defaultColorPalette: FlowDesignColorPalette = {
  abortButtonColor: '#00387d',
  accentColor: '#39f56a',
  appBarBackgroundColor: '#00387d',
  appBarTitleColor: '#ffffff',
  buttonBgColor: '#00387d',
  buttonTextColor: '#ffffff',
  cameraHeaderBgColor: '#000000',
  checkboxAccentColor: '#000000',
  docSelectBgColor: '#ff6600',
  docSelectContainerBgColor: '#f5f5f5',
  incodeHighlightedTextColor: '#000000',
  incodeTextColor: '#000000',
  loadingSpinnerColor: '#02bd9c',
  loadingSpinnerFill: '#02bd9c',
  modalBgColor: '#ffffff',
  ocrFieldsBgColor: '#ffffff',
  ocrInputTextColor: '#ffffff',
  primaryBgColor: '#ffffff',
  primaryTextColor: '#000000',
  progressBarBgColor: '#ff6600',
  reasonActiveBgColor: '#c0f9c8',
  reasonHoverBgColor: '#c0f9c8',
  sdkSpecialTextColor: '#000000',
  themeErrorColor: '#c23b21',
  themeRadioAccentColor: '#89379f',
  themeRadioColor: '#c0f9c8',
  themeSecondaryTextColor: '#00387d',
  /** Mirrored from primaryTextColor (Body text); not shown as its own control. */
  tutorialAccentColor: '#000000',
};

const defaultFormData = {
  logo: null as string | null,
  banner: null as string | null,
  logoSize: 32 as number,
  cancelVerificationText: DEFAULT_CANCEL_VERIFICATION_TEXT,
  buttonColor: '#00387d',
  buttonFontColor: '#ffffff',
  textColor: '#000000',
  fontFamily: 'Arial',
  logoBackgroundColor: '#c8102e',
  showProgressBar: true,
  selectedLanguages: ['English'],
  progressBarOuterColor: '#222222',
  progressBarInnerColor: '#e11d48',
  allowedIdsColor: '#ffffff',
  allowedIdsTextColor: '#ffffff',
  color: { ...defaultColorPalette } as FlowDesignColorPalette,
};

/** Extract a hex color from a Tailwind arbitrary-value class, e.g. "bg-[#FF6600]" → "#FF6600". */
function extractHexFromTwClass(
  cls: string | undefined,
  prop: 'bg' | 'text',
): string | undefined {
  if (!cls) return undefined;
  const regex = prop === 'bg' ? /bg-\[([^\]]+)\]/ : /text-\[([^\]]+)\]/;
  return cls.match(regex)?.[1];
}

function normalizeHex(value: string | undefined): string | undefined {
  if (!value) return value;
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value.toLowerCase() : value;
}

function stableStringify(value: any): string {
  const seen = new WeakSet<object>();

  const walk = (v: any): any => {
    if (v == null) return v;
    if (typeof v !== 'object') return v;
    if (v instanceof Date) return v.toISOString();
    if (v instanceof File) return { name: v.name, size: v.size, type: v.type };
    if (Array.isArray(v)) return v.map(walk);
    if (seen.has(v)) return '[Circular]';
    seen.add(v);

    const out: Record<string, any> = {};
    Object.keys(v)
      .sort()
      .forEach(k => {
        const next = v[k];
        if (typeof next === 'undefined') return;
        out[k] = walk(next);
      });

    return out;
  };

  return JSON.stringify(walk(value));
}

function designToFormData(design: FlowDesignFormPartial | undefined) {
  if (!design) return {};

  const fromAppBarClass = design.appBarClass?.startsWith('bg-[')
    ? extractHexFromTwClass(design.appBarClass, 'bg')
    : design.appBarClass;

  const logoBackgroundColor =
    design.color?.appBarBackgroundColor ??
    fromAppBarClass ??
    defaultFormData.logoBackgroundColor;

  const base = {
    ...(design.appBarLogo ? { logo: design.appBarLogo } : {}),
    ...(design.bannerImg ? { banner: design.bannerImg } : {}),
    ...(design.appBarLogoHeight ? { logoSize: design.appBarLogoHeight } : {}),
    cancelVerificationText: readCancelVerificationText(design),
    logoBackgroundColor: normalizeHex(logoBackgroundColor),
    buttonColor: normalizeHex(
      design.color?.buttonBgColor ??
        design.buttonBgColor ??
        defaultFormData.buttonColor,
    ),
    buttonFontColor: normalizeHex(
      design.color?.buttonTextColor ??
        design.buttonTextColor ??
        defaultFormData.buttonFontColor,
    ),
    textColor:
      normalizeHex(design.color?.primaryTextColor) ??
      normalizeHex(design.textColor) ??
      normalizeHex(defaultFormData.textColor),
    progressBarOuterColor:
      normalizeHex(design.progressBarTrackColor) ??
      normalizeHex(defaultFormData.progressBarOuterColor),
    progressBarInnerColor:
      normalizeHex(design.progressBarFillColor) ??
      normalizeHex(defaultFormData.progressBarInnerColor),
    allowedIdsColor:
      normalizeHex(design.allowedIdsColor) ??
      normalizeHex(defaultFormData.allowedIdsColor),
    allowedIdsTextColor:
      normalizeHex(design.allowedIdsTextColor) ??
      normalizeHex(defaultFormData.allowedIdsTextColor),
  };

  if (!design.color || Object.keys(design.color).length === 0) {
    const bgOnly = normalizeHex(design.backgroundColor);
    if (bgOnly != null) {
      return {
        ...base,
        color: {
          ...defaultColorPalette,
          primaryBgColor: bgOnly,
          tutorialAccentColor:
            normalizeHex(base.textColor as string) ??
            defaultColorPalette.primaryTextColor,
        },
      };
    }
    return base;
  }

  const color: FlowDesignColorPalette = { ...defaultColorPalette };

  (
    Object.keys(defaultColorPalette) as (keyof FlowDesignColorPalette)[]
  ).forEach(key => {
    const v = design.color![key];
    if (v != null) color[key] = (normalizeHex(String(v)) ?? v) as any;
  });
  // Keep palette button background aligned with top-level legacy field.
  color.buttonBgColor =
    normalizeHex(design.color?.buttonBgColor ?? design.buttonBgColor) ??
    color.buttonBgColor;
  color.buttonTextColor =
    normalizeHex(design.color?.buttonTextColor ?? design.buttonTextColor) ??
    color.buttonTextColor;

  const bgFromDesign = normalizeHex(design.backgroundColor);
  if (bgFromDesign != null && design.color?.primaryBgColor == null) {
    color.primaryBgColor = bgFromDesign;
  }

  // Tutorial accent follows body text in the form; keep palette aligned on load.
  color.tutorialAccentColor = (normalizeHex(color.primaryTextColor) ??
    color.primaryTextColor) as string;

  const link =
    normalizeHex(color.themeSecondaryTextColor) ??
    color.themeSecondaryTextColor;
  if (link) (color as any).abortButtonColor = link;

  return { ...base, color };
}

function formDataToDesign(
  formData: typeof defaultFormData,
  logoFile: File | null,
  bannerFile: File | null,
): FlowDesignFormPartial {
  const secondaryColor =
    normalizeHex(formData.color?.themeSecondaryTextColor) ??
    formData.color?.themeSecondaryTextColor ??
    defaultColorPalette.themeSecondaryTextColor ??
    '#00387d';
  const primaryButtonBg =
    normalizeHex(formData.color?.buttonBgColor) ??
    normalizeHex(formData.buttonColor) ??
    formData.buttonColor;

  const primaryButtonText =
    normalizeHex(formData.color?.buttonTextColor) ??
    normalizeHex(formData.buttonFontColor) ??
    formData.buttonFontColor;

  return {
    appBarLogoFile: logoFile,
    ...(bannerFile ? { bannerImageFile: bannerFile } : {}),
    appBarLogoHeight: formData.logoSize ?? 32,
    translations: {
      en: {
        userConsent: {
          cancelVerification:
            (typeof formData.cancelVerificationText === 'string' &&
              formData.cancelVerificationText.trim()) ||
            DEFAULT_CANCEL_VERIFICATION_TEXT,
        },
      },
    },
    appBarClass:
      normalizeHex(formData.color?.appBarBackgroundColor) ??
      normalizeHex(formData.logoBackgroundColor),
    appBarButtonColor: '#ffffff',
    buttonBgColor: primaryButtonBg,
    buttonTextColor: primaryButtonText,
    secondaryButtonColor: normalizeHex(secondaryColor) ?? secondaryColor,
    documentLinkBgColor: primaryButtonBg,
    allowedIdsColor:
      normalizeHex(formData.allowedIdsColor) ?? formData.allowedIdsColor,
    allowedIdsTextColor:
      normalizeHex(formData.allowedIdsTextColor) ??
      formData.allowedIdsTextColor,
    textColor:
      normalizeHex(formData.color?.primaryTextColor) ??
      normalizeHex(formData.textColor) ??
      formData.textColor,
    backgroundColor:
      normalizeHex(formData.color?.primaryBgColor) ??
      formData.color?.primaryBgColor ??
      '#ffffff',
    progressBarTrackColor:
      normalizeHex(formData.progressBarOuterColor) ??
      formData.progressBarOuterColor,
    progressBarFillColor:
      normalizeHex(formData.progressBarInnerColor) ??
      formData.progressBarInnerColor,
    color: formData.color
      ? (() => {
          const c = Object.fromEntries(
            Object.entries(formData.color).map(([k, v]) => [
              k,
              typeof v === 'string' ? (normalizeHex(v) ?? v) : v,
            ]),
          ) as FlowDesignColorPalette;
          const bodyText =
            normalizeHex(formData.color!.primaryTextColor) ??
            normalizeHex(formData.textColor) ??
            formData.textColor;
          c.tutorialAccentColor = bodyText;
          const link =
            normalizeHex(c.themeSecondaryTextColor) ??
            c.themeSecondaryTextColor;
          if (link) c.abortButtonColor = link;
          return c;
        })()
      : undefined,
  };
}

/** Mirrors Basic settings → Document types (ID, PASSPORT, RESIDENCE, DRIVINGLICENSE). */
export type FlowPreviewDocumentTypes = {
  id: boolean;
  passport: boolean;
  residence: boolean;
  drivingLicense: boolean;
};

/** Mirrors POA Accepted documents (INVOICE, BILL, BANK). */
export type PoaPreviewDocumentTypes = {
  invoice: boolean;
  bill: boolean;
  bank: boolean;
};

function DocumentPreviewIconCard({ stroke }: { stroke: string }) {
  return (
    <Box
      sx={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width="20" height="16" viewBox="0 0 24 20" fill="none">
        <rect
          x="1"
          y="3"
          width="22"
          height="14"
          rx="2"
          stroke={stroke}
          strokeWidth="1.5"
          fill="none"
        />
        <rect x="3" y="6" width="4" height="3" rx="0.5" fill={stroke} />
        <line x1="9" y1="7" x2="15" y2="7" stroke={stroke} strokeWidth="1" />
        <line x1="9" y1="9" x2="13" y2="9" stroke={stroke} strokeWidth="1" />
        <line x1="3" y1="12" x2="21" y2="12" stroke={stroke} strokeWidth="1" />
        <line x1="3" y1="14" x2="12" y2="14" stroke={stroke} strokeWidth="1" />
      </svg>
    </Box>
  );
}

function DocumentPreviewIconPassport({ stroke }: { stroke: string }) {
  return (
    <Box
      sx={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
        <rect
          x="1"
          y="1"
          width="14"
          height="18"
          rx="1"
          stroke={stroke}
          strokeWidth="1.5"
          fill="none"
        />
        <circle
          cx="8"
          cy="7"
          r="2.5"
          stroke={stroke}
          strokeWidth="1"
          fill="none"
        />
        <path
          d="M4 15c0-2.2 1.8-4 4-4s4 1.8 4 4"
          stroke={stroke}
          strokeWidth="1"
          fill="none"
        />
        <line x1="4" y1="2" x2="12" y2="2" stroke={stroke} strokeWidth="1" />
      </svg>
    </Box>
  );
}

interface FlowCustomContentProps {
  design?: FlowDesignFormPartial;
  onDesignChange?: (updates: FlowDesignFormPartial) => void;
  /** From Basic settings — controls which rows appear in the Document live preview. */
  previewDocumentTypes?: FlowPreviewDocumentTypes | PoaPreviewDocumentTypes;
}

function isPoaPreviewDocumentTypes(
  dt: FlowPreviewDocumentTypes | PoaPreviewDocumentTypes,
): dt is PoaPreviewDocumentTypes {
  return 'invoice' in dt || 'bill' in dt || 'bank' in dt;
}

const FlowCustomContent = ({
  design,
  onDesignChange,
  previewDocumentTypes,
}: FlowCustomContentProps) => {
  const [previewStep, setPreviewStep] = useState<PreviewStepId>('terms');
  const [formData, setFormData] = useState({
    ...defaultFormData,
    ...designToFormData(design),
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const logoSrc = formData.logo || design?.appBarLogo || null;
  const safeLogoSrc = useSafeLogoSrc(logoSrc);
  const sdkColorsEnabled = false;

  const lastEmittedDesignKeyRef = useRef<string | null>(null);
  const isEditingColorRef = useRef(false);

  const showSection = (sectionId: string) =>
    SECTIONS_BY_PREVIEW_STEP[previewStep].includes(sectionId);

  const formColorKey = useMemo(
    () => stableStringify(formData.color),
    [formData.color],
  );

  const documentPreviewRows = useMemo(() => {
    const stroke =
      formData.allowedIdsColor ?? formData.allowedIdsTextColor ?? '#666';
    const rows: Array<{ key: string; label: string; icon: React.ReactNode }> =
      [];

    if (
      previewDocumentTypes &&
      isPoaPreviewDocumentTypes(previewDocumentTypes)
    ) {
      const dt = previewDocumentTypes;
      if (dt.invoice) {
        rows.push({
          key: 'INVOICE',
          label: 'Invoice',
          icon: <DocumentPreviewIconCard stroke={stroke} />,
        });
      }
      if (dt.bill) {
        rows.push({
          key: 'BILL',
          label: 'Utility bill',
          icon: <DocumentPreviewIconCard stroke={stroke} />,
        });
      }
      if (dt.bank) {
        rows.push({
          key: 'BANK',
          label: 'Bank statement',
          icon: <DocumentPreviewIconCard stroke={stroke} />,
        });
      }
      return rows;
    }

    const dt: FlowPreviewDocumentTypes = (previewDocumentTypes as
      | FlowPreviewDocumentTypes
      | undefined) ?? {
      id: true,
      passport: true,
      residence: true,
      drivingLicense: true,
    };
    if (dt.id) {
      rows.push({
        key: 'ID',
        label: 'Identity card',
        icon: <DocumentPreviewIconCard stroke={stroke} />,
      });
    }
    if (dt.passport) {
      rows.push({
        key: 'PASSPORT',
        label: 'Passport',
        icon: <DocumentPreviewIconPassport stroke={stroke} />,
      });
    }
    if (dt.residence) {
      rows.push({
        key: 'RESIDENCE',
        label: 'Residence permit',
        icon: <DocumentPreviewIconCard stroke={stroke} />,
      });
    }
    if (dt.drivingLicense) {
      rows.push({
        key: 'DRIVINGLICENSE',
        label: 'Driving license',
        icon: <DocumentPreviewIconCard stroke={stroke} />,
      });
    }
    return rows;
  }, [
    previewDocumentTypes,
    formData.allowedIdsColor,
    formData.allowedIdsTextColor,
  ]);

  const designColorKey = stableStringify(design?.color ?? {});
  const designTranslationsKey = stableStringify(design?.translations ?? {});

  useEffect(() => {
    if (!design || isEditingColorRef.current) return;

    const incoming = designToFormData(design);

    setFormData(prev => {
      const next = { ...prev, ...incoming };
      if (stableStringify(next) === stableStringify(prev)) return prev;
      return next;
    });

    lastEmittedDesignKeyRef.current = stableStringify(design);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    design?.appBarLogo,
    design?.appBarClass,
    design?.buttonBgColor,
    design?.buttonTextColor,
    design?.textColor,
    design?.progressBarTrackColor,
    design?.progressBarFillColor,
    design?.backgroundColor,
    designTranslationsKey,
    designColorKey,
  ]);

  useEffect(() => {
    if (!onDesignChange) return;

    const nextDesign = formDataToDesign(formData, logoFile, bannerFile);
    const nextKey = stableStringify(nextDesign);
    const currentPropKey = stableStringify(design);

    if (nextKey === currentPropKey) return;
    if (lastEmittedDesignKeyRef.current === nextKey) return;

    lastEmittedDesignKeyRef.current = nextKey;
    onDesignChange(nextDesign);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.logoBackgroundColor,
    formData.logoSize,
    formData.banner,
    formData.buttonColor,
    formData.buttonFontColor,
    formData.textColor,
    formData.progressBarOuterColor,
    formData.progressBarInnerColor,
    formData.allowedIdsColor,
    formData.allowedIdsTextColor,
    formData.cancelVerificationText,
    formColorKey,
    logoFile,
    bannerFile,
  ]);

  const beginColorEdit = () => {
    isEditingColorRef.current = true;
  };

  const endColorEdit = () => {
    window.setTimeout(() => {
      isEditingColorRef.current = false;
    }, 0);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]:
        typeof value === 'string' ? (normalizeHex(value) ?? value) : value,
    }));
  };

  const handlePrimaryButtonBgChange = (value: string) => {
    const normalized = normalizeHex(value) ?? value;
    setFormData(prev => ({
      ...prev,
      // Legacy field used across current preview styles.
      buttonColor: normalized,
      // Canonical SDK palette field.
      color: { ...prev.color, buttonBgColor: normalized },
    }));
  };

  const handlePrimaryButtonTextChange = (value: string) => {
    const normalized = normalizeHex(value) ?? value;
    setFormData(prev => ({
      ...prev,
      // Legacy field used across current preview styles.
      buttonFontColor: normalized,
      // Canonical SDK palette field.
      color: { ...prev.color, buttonTextColor: normalized },
    }));
  };

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: 'logo' | 'banner',
  ) => {
    const file = event.target.files?.[0];

    if (
      file &&
      (file.type === 'image/svg+xml' ||
        file.type === 'image/png' ||
        file.type === 'image/jpeg')
    ) {
      if (field === 'logo') {
        setLogoFile(file);
      } else {
        setBannerFile(file);
      }

      const reader = new FileReader();
      reader.onload = e => {
        handleInputChange(field, e.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoRemove = () => {
    setFormData(prev => ({ ...prev, logo: null }));
    setLogoFile(null);

    if (onDesignChange) {
      onDesignChange(
        formDataToDesign({ ...formData, logo: null }, null, bannerFile),
      );
    }
  };

  const handleBannerRemove = () => {
    setFormData(prev => ({ ...prev, banner: null }));
    setBannerFile(null);

    if (onDesignChange) {
      onDesignChange({
        ...formDataToDesign({ ...formData, banner: null }, logoFile, null),
        // Explicitly signal removal so the parent can send bannerImage='' on save.
        bannerImg: null,
        bannerImageFile: null,
      });
    }
  };

  const handleLanguageChange = (language: string) => {
    setFormData(prev => ({
      ...prev,
      selectedLanguages: prev.selectedLanguages.includes(language)
        ? prev.selectedLanguages.filter(l => l !== language)
        : [...prev.selectedLanguages, language],
    }));
  };

  const handleColorPaletteChange = (
    key: keyof FlowDesignColorPalette,
    value: string,
  ) => {
    const normalized = normalizeHex(value) ?? value;

    setFormData(prev => {
      const nextColor: FlowDesignColorPalette = {
        ...prev.color,
        [key]: normalized,
      };
      if (key === 'primaryTextColor') {
        nextColor.tutorialAccentColor = normalized;
      }
      if (key === 'primaryBgColor') {
        nextColor.docSelectContainerBgColor = normalized;
      }
      if (key === 'themeSecondaryTextColor') {
        nextColor.abortButtonColor = normalized;
      }
      return {
        ...prev,
        ...(key === 'primaryTextColor' ? { textColor: normalized } : {}),
        color: nextColor,
      };
    });
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={6}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          Custom Flow Design
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Fields below apply to the current preview step. Change the step using
          the buttons in the preview.
        </Typography>

        {showSection('branding') && (
          <Card variant="outlined" sx={designCardSx}>
            <CardContent
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                m: 1.5,
                '&:last-child': { pb: 2.5 },
              }}
            >
              <SectionTitle icon={ImageIcon}>Branding</SectionTitle>

              <UploadZone
                label="Upload Logo"
                hint="SVG, PNG or JPG. Shown in top navigation."
                value={formData.logo}
                onUpload={e => handleFileUpload(e, 'logo')}
                onRemove={handleLogoRemove}
              />

              <Box sx={{ mt: 2 }}>
                <ColorGrid>
                  <ColorRow
                    label="Header / logo background"
                    value={
                      formData.color?.appBarBackgroundColor ??
                      formData.logoBackgroundColor ??
                      defaultColorPalette.appBarBackgroundColor ??
                      '#00387d'
                    }
                    onChange={v =>
                      handleColorPaletteChange('appBarBackgroundColor', v)
                    }
                    onEditStart={beginColorEdit}
                    onEditEnd={endColorEdit}
                    helperText="Header area"
                  />
                </ColorGrid>
              </Box>

              <Box sx={{ mt: 2 }}>
                <UploadZone
                  label="Upload Banner"
                  hint="Optional. Shown above the main content."
                  value={formData.banner}
                  onUpload={e => handleFileUpload(e, 'banner')}
                  onRemove={handleBannerRemove}
                />
              </Box>
            </CardContent>
          </Card>
        )}

        {showSection('progressBar') && formData.showProgressBar && (
          <Card variant="outlined" sx={designCardSx}>
            <CardContent
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                m: 1.5,
                '&:last-child': { pb: 2.5 },
              }}
            >
              <SectionTitle icon={PaletteIcon}>Progress bar</SectionTitle>

              <ColorGrid>
                <ColorRow
                  label="Fill (progress)"
                  value={
                    formData.color?.accentColor ??
                    defaultColorPalette.accentColor ??
                    '#ff6600'
                  }
                  onChange={v => handleColorPaletteChange('accentColor', v)}
                  onEditStart={beginColorEdit}
                  onEditEnd={endColorEdit}
                  helperText="Progress fill"
                />
              </ColorGrid>
            </CardContent>
          </Card>
        )}

        {showSection('colorsTypography') && (
          <Card variant="outlined" sx={designCardSx}>
            <CardContent
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                m: 1.5,
                '&:last-child': { pb: 2.5 },
              }}
            >
              <SectionTitle icon={TextFieldsIcon}>
                Colors & typography
              </SectionTitle>

              <ColorGrid>
                <ColorRow
                  label="Body text"
                  value={
                    formData.color?.primaryTextColor ??
                    formData.textColor ??
                    defaultColorPalette.primaryTextColor ??
                    '#000000'
                  }
                  onChange={v =>
                    handleColorPaletteChange('primaryTextColor', v)
                  }
                  onEditStart={beginColorEdit}
                  onEditEnd={endColorEdit}
                  helperText="Default text"
                />

                <ColorRow
                  label="Page background"
                  value={
                    formData.color?.primaryBgColor ??
                    defaultColorPalette.primaryBgColor ??
                    '#ffffff'
                  }
                  onChange={v => handleColorPaletteChange('primaryBgColor', v)}
                  onEditStart={beginColorEdit}
                  onEditEnd={endColorEdit}
                  helperText="Screen background"
                />

                <ColorRow
                  label="CTA background"
                  value={
                    formData.color?.buttonBgColor ??
                    formData.buttonColor ??
                    defaultColorPalette.buttonBgColor
                  }
                  onChange={handlePrimaryButtonBgChange}
                  onEditStart={beginColorEdit}
                  onEditEnd={endColorEdit}
                  helperText="Primary button"
                />

                <ColorRow
                  label="CTA text"
                  value={
                    formData.color?.buttonTextColor ??
                    formData.buttonFontColor ??
                    defaultColorPalette.buttonTextColor
                  }
                  onChange={handlePrimaryButtonTextChange}
                  onEditStart={beginColorEdit}
                  onEditEnd={endColorEdit}
                  helperText="Button label"
                />

                <ColorRow
                  label="Link color"
                  value={
                    formData.color?.themeSecondaryTextColor ??
                    defaultColorPalette.themeSecondaryTextColor ??
                    '#00387d'
                  }
                  onChange={v =>
                    handleColorPaletteChange('themeSecondaryTextColor', v)
                  }
                  onEditStart={beginColorEdit}
                  onEditEnd={endColorEdit}
                  helperText="Links & secondary"
                />

                <ColorRow
                  label="Checkbox (terms)"
                  value={
                    formData.color?.checkboxAccentColor ??
                    defaultColorPalette.checkboxAccentColor ??
                    '#000000'
                  }
                  onChange={v =>
                    handleColorPaletteChange('checkboxAccentColor', v)
                  }
                  onEditStart={beginColorEdit}
                  onEditEnd={endColorEdit}
                  helperText="Terms checkbox"
                />
              </ColorGrid>

              <TextField
                fullWidth
                label="Cancel verification"
                value={formData.cancelVerificationText ?? ''}
                onChange={e =>
                  handleInputChange('cancelVerificationText', e.target.value)
                }
                variant="outlined"
                size="small"
                sx={{ mt: 2 }}
              />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'flex-start' },
                  gap: 2,
                  mt: 2,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ColorGrid>
                    <ColorRow
                      label="Spinner fill"
                      value={
                        formData.color?.loadingSpinnerColor ??
                        defaultColorPalette.loadingSpinnerColor ??
                        '#02bd9c'
                      }
                      onChange={v =>
                        handleColorPaletteChange('loadingSpinnerColor', v)
                      }
                      onEditStart={beginColorEdit}
                      onEditEnd={endColorEdit}
                      helperText="Spinner arc"
                    />
                    <ColorRow
                      label="Spinner track"
                      value={
                        formData.color?.loadingSpinnerFill ??
                        defaultColorPalette.loadingSpinnerFill ??
                        '#02bd9c'
                      }
                      onChange={v =>
                        handleColorPaletteChange('loadingSpinnerFill', v)
                      }
                      onEditStart={beginColorEdit}
                      onEditEnd={endColorEdit}
                      helperText="Spinner track"
                    />
                  </ColorGrid>
                </Box>
                <LoadingSpinnerPreview
                  spinnerColor={
                    formData.color?.loadingSpinnerColor ??
                    defaultColorPalette.loadingSpinnerColor ??
                    '#02bd9c'
                  }
                  spinnerFill={
                    formData.color?.loadingSpinnerFill ??
                    defaultColorPalette.loadingSpinnerFill ??
                    '#02bd9c'
                  }
                />
              </Box>

              {/* <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Font family</InputLabel>
                <Select
                  value={formData.fontFamily}
                  label="Font family"
                  onChange={e =>
                    handleInputChange('fontFamily', e.target.value)
                  }
                >
                  {fonts.map(font => (
                    <MenuItem
                      key={font}
                      value={font}
                      style={{ fontFamily: font }}
                    >
                      {font}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}
            </CardContent>
          </Card>
        )}

        {showSection('languages') && (
          <Card
            variant="outlined"
            sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}
          >
            <CardContent sx={{ '&:last-child': { pb: 3 } }}>
              <SectionTitle icon={LanguageIcon}>Languages</SectionTitle>

              <FormGroup row sx={{ gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.selectedLanguages.includes('English')}
                      onChange={() => handleLanguageChange('English')}
                    />
                  }
                  label="English"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.selectedLanguages.includes('German')}
                      onChange={() => handleLanguageChange('German')}
                    />
                  }
                  label="German"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.selectedLanguages.includes('French')}
                      onChange={() => handleLanguageChange('French')}
                    />
                  }
                  label="French"
                />
              </FormGroup>
            </CardContent>
          </Card>
        )}

        {showSection('sdkColors') && (
          <Card variant="outlined" sx={designCardSx}>
            <CardContent
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                m: 1.5,
                '&:last-child': { pb: 2.5 },
              }}
            >
              <SectionTitle icon={PaletteIcon}>
                Document Select Colors
              </SectionTitle>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Colors used by the document selection UI in the flow.
              </Typography>

              <ColorGrid>
                <ColorRow
                  label="Background color"
                  value={
                    formData.color?.docSelectBgColor ??
                    defaultColorPalette.docSelectBgColor ??
                    '#ff6600'
                  }
                  onChange={v =>
                    handleColorPaletteChange('docSelectBgColor', v)
                  }
                  onEditStart={beginColorEdit}
                  onEditEnd={endColorEdit}
                  helperText="Doc select bg"
                />

                <ColorRow
                  label="Icon color"
                  value={formData.allowedIdsColor ?? '#ffffff'}
                  onChange={v => handleInputChange('allowedIdsColor', v)}
                  onEditStart={beginColorEdit}
                  onEditEnd={endColorEdit}
                  helperText="Document icons"
                />

                <ColorRow
                  label="Text color"
                  value={formData.allowedIdsTextColor ?? '#ffffff'}
                  onChange={v => handleInputChange('allowedIdsTextColor', v)}
                  onEditStart={beginColorEdit}
                  onEditEnd={endColorEdit}
                  helperText="Document labels"
                />
              </ColorGrid>
            </CardContent>
          </Card>
        )}

        {sdkColorsEnabled && showSection('sdkColors') && (
          <Card
            variant="outlined"
            sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}
          >
            <CardContent sx={{ '&:last-child': { pb: 3 } }}>
              <SectionTitle icon={PaletteIcon}>SDK / Theme colors</SectionTitle>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Colors passed as design.color to the flow (abort, accent, app
                bar, buttons, progress, theme, etc.).
              </Typography>

              <Grid container spacing={2}>
                {COLOR_PALETTE_ENTRIES.map(({ key, label }) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <ColorRow
                      label={label}
                      value={
                        formData.color?.[key] ??
                        defaultColorPalette[key] ??
                        '#000000'
                      }
                      onChange={v => handleColorPaletteChange(key, v)}
                      onEditStart={beginColorEdit}
                      onEditEnd={endColorEdit}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'sticky',
            top: 24,
            height: 'fit-content',
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ mb: 2, letterSpacing: 1 }}
          >
            Preview
          </Typography>

          <ButtonGroup size="small" sx={{ mb: 2, flexWrap: 'wrap' }}>
            {PREVIEW_STEPS.map((step, idx) => (
              <Button
                key={step}
                variant={previewStep === step ? 'contained' : 'outlined'}
                onClick={() => setPreviewStep(step)}
              >
                {idx + 1}. {step === 'terms' ? 'Terms' : 'Document'}
              </Button>
            ))}
          </ButtonGroup>

          <PhonePreview>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor:
                  formData.color?.primaryBgColor ??
                  defaultColorPalette.primaryBgColor ??
                  '#ffffff',
                fontFamily: formData.fontFamily,
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  backgroundColor:
                    (formData.color?.appBarBackgroundColor ??
                      formData.logoBackgroundColor) ||
                    '#00387d',
                  color: '#fff',
                  padding: '0px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                <span>9:41</span>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Box
                    sx={{
                      width: '18px',
                      height: '7px',
                      border: '1px solid #fff',
                      borderRadius: '2px',
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        width: '2px',
                        height: '4px',
                        backgroundColor: '#fff',
                        position: 'absolute',
                        right: '-3px',
                        top: '2px',
                        borderRadius: '1px',
                      }}
                    />
                    <Box
                      sx={{
                        width: '14px',
                        height: '6px',
                        backgroundColor: '#fff',
                        borderRadius: '1px',
                        margin: '1px',
                      }}
                    />
                  </Box>

                  <Box
                    sx={{ width: '15px', height: '11px', position: 'relative' }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        border: '1px solid #fff',
                        borderRadius: '2px',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '2px',
                        left: '2px',
                        width: '3px',
                        height: '3px',
                        backgroundColor: '#fff',
                        borderRadius: '1px',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '2px',
                        left: '6px',
                        width: '3px',
                        height: '5px',
                        backgroundColor: '#fff',
                        borderRadius: '1px',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '2px',
                        left: '10px',
                        width: '3px',
                        height: '7px',
                        backgroundColor: '#fff',
                        borderRadius: '1px',
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      width: '24px',
                      height: '11px',
                      border: '1px solid #fff',
                      borderRadius: '2px',
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        width: '20px',
                        height: '7px',
                        backgroundColor: '#fff',
                        borderRadius: '1px',
                        margin: '1px',
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  backgroundColor:
                    (formData.color?.appBarBackgroundColor ??
                      formData.logoBackgroundColor) ||
                    '#00387d',
                  color: formData.color?.appBarTitleColor || '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  minHeight: '48px',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{ position: 'absolute', left: '16px', cursor: 'pointer' }}
                  onClick={() =>
                    previewStep === 'document'
                      ? setPreviewStep('terms')
                      : undefined
                  }
                  role="button"
                  aria-label="Back"
                >
                  <Box
                    sx={{
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {previewStep === 'terms' ? (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          d="M19 12H5M12 19l-7-7 7-7"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </Box>
                </Box>

                <Box sx={{ flex: 1, textAlign: 'center' }}>
                  {safeLogoSrc ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={safeLogoSrc}
                        alt="Logo"
                        style={{
                          height: '1.4rem',
                          maxWidth: '160px',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: '18px',
                        fontWeight: '600',
                        fontFamily: formData.fontFamily,
                      }}
                    >
                      Identity verification
                    </Typography>
                  )}
                </Box>
              </Box>

              {previewStep === 'terms' && (
                <Box
                  sx={{
                    padding: '24px 16px',
                    flex: 1,
                    backgroundColor:
                      formData.color?.primaryBgColor || '#ffffff',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '18px',
                      fontWeight: '700',
                      mb: 2,
                      color:
                        (formData.color?.primaryTextColor ??
                          formData.textColor) ||
                        '#333',
                      fontFamily: formData.fontFamily,
                    }}
                  >
                    {resolveClientTranslationKey('client.userConsent.title') ??
                      'Terms & Conditions'}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      mb: 3,
                    }}
                  >
                    <Checkbox
                      size="small"
                      sx={{
                        mt: '-2px',
                        color:
                          formData.color?.checkboxAccentColor ||
                          formData.buttonColor,
                        '&.Mui-checked': {
                          color:
                            formData.color?.checkboxAccentColor ||
                            formData.buttonColor,
                        },
                      }}
                    />

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          lineHeight: 1.45,
                          color:
                            (formData.color?.primaryTextColor ??
                              formData.textColor) ||
                            '#333',
                          fontFamily: formData.fontFamily,
                          mb: 1.25,
                        }}
                      >
                        {resolveClientTranslationKey(
                          'client.userConsent.consentMessage',
                        ) ??
                          'I hereby consent to Betsson processing my biometric data for the purpose of verifying my identity.'}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: '14px',
                          color:
                            (formData.color?.primaryTextColor ??
                              formData.textColor) ||
                            '#333',
                          fontFamily: formData.fontFamily,
                        }}
                      >
                        {resolveClientTranslationKey(
                          'client.userConsent.moreDetails',
                        ) ?? 'Find more details in the '}
                        <Box
                          component="span"
                          sx={{
                            color:
                              formData.color?.themeSecondaryTextColor ||
                              formData.buttonColor,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          {resolveClientTranslationKey(
                            'client.userConsent.privacyPolicy',
                          ) ?? 'privacy policy'}
                        </Box>{' '}
                        {resolveClientTranslationKey(
                          'client.userConsent.and',
                        ) ?? 'and'}{' '}
                        <Box
                          component="span"
                          sx={{
                            color:
                              formData.color?.themeSecondaryTextColor ||
                              formData.buttonColor,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          {resolveClientTranslationKey(
                            'client.userConsent.termsOfService',
                          ) ?? 'general terms and conditions'}
                        </Box>
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 'auto', pt: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => setPreviewStep('document')}
                      sx={{
                        textTransform: 'none',
                        backgroundColor:
                          formData.buttonColor ||
                          formData.color?.buttonBgColor ||
                          '#404040',
                        color:
                          formData.buttonFontColor ||
                          formData.color?.buttonTextColor ||
                          '#fff',
                        fontWeight: '600',
                        fontSize: '16px',
                        borderRadius: '8px',
                        padding: '12px',
                        fontFamily: formData.fontFamily,
                        '&:hover': {
                          backgroundColor: formData.buttonColor || '#333',
                        },
                      }}
                    >
                      {resolveClientTranslationKey(
                        'client.userConsent.startVerification',
                      ) ?? 'Start verification'}
                    </Button>

                    <Typography
                      component="button"
                      onClick={() => setPreviewStep('terms')}
                      sx={{
                        display: 'block',
                        width: '100%',
                        mt: 1.5,
                        fontSize: '14px',
                        color:
                          formData.color?.themeSecondaryTextColor ??
                          formData.buttonColor,
                        fontFamily: formData.fontFamily,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'center',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {formData.cancelVerificationText?.trim() ||
                        DEFAULT_CANCEL_VERIFICATION_TEXT}
                    </Typography>
                  </Box>
                </Box>
              )}

              {previewStep === 'document' && (
                <>
                  {formData.showProgressBar && (
                    <Box
                      sx={{
                        backgroundColor: '#e0e0e0',
                        height: '4px',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor:
                            formData.color?.accentColor ||
                            defaultColorPalette.accentColor ||
                            '#ff6600',
                          height: '100%',
                          width: '33%',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      padding: '24px 16px',
                      flex: 1,
                      backgroundColor:
                        formData.color?.primaryBgColor || '#ffffff',
                      overflow: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {formData.banner && (
                      <Box sx={{ mb: 3, flexShrink: 0 }}>
                        <img
                          src={formData.banner}
                          alt="Banner"
                          style={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '80px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                          }}
                        />
                      </Box>
                    )}

                    <Box sx={{ flex: 1, minHeight: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          mb: 1,
                          color:
                            (formData.color?.primaryTextColor ??
                              formData.textColor) ||
                            '#000',
                          fontFamily: formData.fontFamily,
                        }}
                      >
                        What type of document you like to use?
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: '14px',
                          color:
                            (formData.color?.primaryTextColor ??
                              formData.textColor) ||
                            '#333',
                          fontFamily: formData.fontFamily,
                          mb: 2,
                          lineHeight: 1.4,
                        }}
                      >
                        We need to confirm with a valid document that the
                        account belongs to you.
                      </Typography>

                      {documentPreviewRows.length === 0 ? (
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: 'text.secondary',
                            fontFamily: formData.fontFamily,
                            py: 2,
                          }}
                        >
                          No document types selected. Enable ID, Passport,
                          Residence, and/or Driving license under Basic settings
                          → Document types.
                        </Typography>
                      ) : (
                        documentPreviewRows.map(doc => (
                          <Box
                            key={doc.key}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              backgroundColor:
                                formData.color?.docSelectBgColor ??
                                formData.color?.docSelectContainerBgColor ??
                                '#f5f5f5',
                              borderRadius: '10px',
                              padding: '16px',
                              mb: 2,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                              border: '1px solid #e8e8e8',
                              '&:hover': {
                                backgroundColor:
                                  formData.color?.docSelectBgColor ??
                                  formData.color?.reasonHoverBgColor ??
                                  '#eeeeee',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                              }}
                            >
                              {doc.icon}
                              <Typography
                                sx={{
                                  fontSize: '16px',
                                  color:
                                    formData.allowedIdsTextColor ??
                                    formData.color?.primaryTextColor ??
                                    formData.textColor ??
                                    '#000',
                                  fontFamily: formData.fontFamily,
                                }}
                              >
                                {doc.label}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                color: formData.allowedIdsColor ?? '#ccc',
                                fontSize: '18px',
                                fontWeight: 'bold',
                              }}
                            >
                              ›
                            </Box>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </PhonePreview>
        </Box>
      </Grid>
    </Grid>
  );
};

export default FlowCustomContent;
